/**
 * /reset — complete the identity-wide password reset using the token
 * delivered by email (or surfaced via dev_token in non-production).
 *
 * Migration note: 1:1 port of `frontend/src/app/(auth)/reset/page.tsx`.
 * next/link → <Link>; useSearchParams → Route.useSearch() for the
 * `?email=&token=` hydration. The form keeps server-side validation as
 * the authority — every field error from the backend lands on its
 * matching <FormMessage /> via handleAuthError.
 */

import { useEffect, useState } from "react";
import {
  Link,
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { authApi } from "@/lib/api";
import { resetSchema, type ResetValues } from "@/lib/auth/schemas";
import { handleAuthError } from "@/lib/auth/form-errors";

const FIELDS = ["email", "token", "new_password"] as const;

const resetSearchSchema = z.object({
  email: z.string().optional(),
  token: z.string().optional(),
});

export const Route = createFileRoute("/_auth/reset")({
  validateSearch: resetSearchSchema,
  component: ResetPage,
});

function ResetPage() {
  const navigate = useNavigate();
  const { email: emailQ, token: tokenQ } = Route.useSearch();
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<ResetValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      email: "",
      token: "",
      new_password: "",
      confirm_password: "",
    },
  });

  // Hydrate from the link the user followed (?email=&token=).
  // Run only when the typed params change; setValue is a stable
  // identity but biome/react-hooks/exhaustive-deps prefers the explicit
  // dep.
  useEffect(() => {
    if (emailQ) form.setValue("email", emailQ.toLowerCase());
    if (tokenQ) form.setValue("token", tokenQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailQ, tokenQ]);

  async function onSubmit(values: ResetValues) {
    setSubmitting(true);
    try {
      await authApi.resetPassword({
        email: values.email,
        token: values.token,
        new_password: values.new_password,
      });
      toast.success("Password updated — sign in with your new password.");
      void navigate({ to: "/login", replace: true });
    } catch (err) {
      handleAuthError(err, form.setError, FIELDS);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">Set a new password</CardTitle>
        <CardDescription>
          Choose a strong password you don&apos;t use elsewhere.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="token"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reset token</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Paste the token from your email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="new_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="••••••••••••"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    At least 12 characters, mixing 3 of: lowercase,
                    uppercase, digit, symbol.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirm_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm new password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="••••••••••••"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Update password
            </Button>
          </form>
        </Form>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          <Link
            to="/login"
            className="font-medium text-primary hover:underline"
          >
            Back to sign in
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}
